import { Box, FormGroup, FormHelperText, FormLabel, Typography } from '@mui/material';
import { styled } from '@mui/system';
import React, { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import ReactQuill from 'react-quill';

import 'react-quill/dist/quill.snow.css';

interface InputRichTextProps {
    control: any;
    name: string;
    label?: string;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    fullWidth?: boolean;
    viewOnly?: boolean;
    viewText?: React.ReactNode | string;
    defaultValue?: string | null;
    maxLength?: number;
    rules?: { [key: string]: (value: any) => string | boolean };
    onChange?: (value: string) => void;
    containerSx?: any;
    labelSx?: any;
}

const modules = {
    toolbar: {
        container: [
            [{ header: '1' }, { header: '2' }],
            [{ size: [] }],
            ['bold', 'italic', 'underline', 'strike', 'blockquote'],
            [{ list: 'ordered' }, { list: 'bullet' }, { indent: '-1' }, { indent: '+1' }],
            ['clean'],
            ['html'],
        ],
    },
    clipboard: {
        matchVisual: false,
    },
};

const formats = [
    'header',
    'bold',
    'italic',
    'size',
    'underline',
    'strike',
    'blockquote',
    'list',
    'bullet',
    'indent',
    'link',
    'html',
];

export const Article = styled('article')(({ theme }) => ({
    whiteSpace: 'pre-line',
    '& p': {
        lineHeight: '1.5em',
        whiteSpace: 'pre-line',
        marginTop: 0,
        [theme.breakpoints.up('xs')]: {
            fontSize: '14px',
            marginBottom: '0.5em',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '16px',
            marginBottom: '1em',
        },
    },
    '& .ql-size-small': {
        [theme.breakpoints.up('xs')]: {
            fontSize: '12px',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '13px',
        },
    },
    '& .ql-size-normal': {
        [theme.breakpoints.up('xs')]: {
            fontSize: '14px',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '16px',
        },
    },
    '& .ql-size-large': {
        [theme.breakpoints.up('xs')]: {
            fontSize: '18px',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '20px',
        },
    },
    '& .ql-size-huge': {
        [theme.breakpoints.up('xs')]: {
            fontSize: '22px',
        },
        [theme.breakpoints.up('md')]: {
            fontSize: '32px',
        },
    },
    '& .ql-indent-1': {
        paddingLeft: '3em',
    },
    '& .ql-indent-2': {
        paddingLeft: '6em',
    },
    '& .ql-indent-3': {
        paddingLeft: '9em',
    },
    '& .ql-indent-4': {
        paddingLeft: '12em',
    },
    '& .ql-indent-5': {
        paddingLeft: '15em',
    },
    '& .ql-indent-6': {
        paddingLeft: '18em',
    },
    '& .ql-indent-7': {
        paddingLeft: '21em',
    },
    '& strong': {
        fontWeight: 700,
        '& em': {
            fontWeight: 700,
        },
        '& i': {
            fontWeight: 700,
        },
    },
    '& blockquote': {
        borderLeft: '4px solid #706D85',
        margin: '1em 0',
        paddingLeft: '1em',
        paddingTop: '0.5em',
        paddingBottom: '0.5em',
    },
    '& ul': {
        '& li': {
            marginBottom: 8,
            marginTop: 8,
            lineHeight: '1.5em',
            '&:last-child': {
                marginBottom: 0,
            },
            [theme.breakpoints.up('xs')]: {
                fontSize: '14px',
            },
            [theme.breakpoints.up('md')]: {
                fontSize: '16px',
            },
        },
    },
}));

const stripHtml = (value: string) =>
    value
        .replace(/<[^>]*>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

const buildValidator = (rules?: { [key: string]: (value: any) => string | boolean }, maxLength?: number) => {
    if (!rules && !maxLength) return undefined;

    return (value: string) => {
        if (typeof maxLength === 'number') {
            const length = stripHtml(value || '').length;
            if (length > maxLength) {
                return `Veuillez entrer moins de ${maxLength} caractères`;
            }
        }

        if (rules) {
            for (const key of Object.keys(rules)) {
                const result = rules[key]?.(value);
                if (result !== true) return result;
            }
        }

        return true;
    };
};

const InputRichText: React.FC<InputRichTextProps> = (props) => {
    const validate = useMemo(() => buildValidator(props.rules, props.maxLength), [props.rules, props.maxLength]);

    return (
        <Controller
            control={props.control}
            name={props.name}
            defaultValue={props.defaultValue ?? ''}
            rules={{
                validate,
                required: props.required,
            }}
            render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                <FormGroup sx={{ width: '100%', ...(props.fullWidth ? { maxWidth: '100%' } : {}), ...props.containerSx }}>
                <Box
                    className={`${error ? 'ql-error' : ''} ${props.disabled ? 'ql-disabled' : ''}`}
                    sx={{
                        width: '100%',
                        '& .ql-container.ql-snow': (theme) => ({
                            border: 'none',
                            borderRadius: '8px',
                            minHeight: 180,
                            backgroundColor: theme.palette.mode === 'dark' ? '#282C30' : '#F9F9F9',
                        }),
                        '& .ql-toolbar.ql-snow': (theme) => ({
                            border: 'none',
                            borderRadius: '8px',
                            backgroundColor: theme.palette.mode === 'dark' ? '#282C30' : '#F9F9F9',
                            marginBottom: '8px',
                        }),
                        '&.ql-disabled .ql-container.ql-snow': (theme) => ({
                            backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#F5F5F5',
                        }),
                        '&.ql-disabled .ql-toolbar.ql-snow': (theme) => ({
                            backgroundColor: theme.palette.mode === 'dark' ? '#424242' : '#F5F5F5',
                        }),
                        '& .ql-toolbar.ql-snow .ql-picker': (theme) => ({
                            color: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow .ql-picker-label': (theme) => ({
                            color: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow .ql-picker-item': (theme) => ({
                            color: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow button': (theme) => ({
                            color: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow .ql-stroke': (theme) => ({
                            stroke: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow .ql-fill': (theme) => ({
                            fill: theme.palette.text.primary,
                        }),
                        '& .ql-toolbar.ql-snow .ql-picker-options': (theme) => ({
                            backgroundColor: theme.palette.background.paper,
                            color: theme.palette.text.primary,
                            border: 'none',
                        }),
                        '& .ql-editor': (theme) => ({
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: '14px',
                            fontWeight: 500,
                            lineHeight: '20px',
                            padding: '14px 16px',
                        }),
                        // placeholder styles
                        '& .ql-editor.ql-blank::before': {
                            fontFamily: "'Plus Jakarta Sans', sans-serif",
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#939393',
                            lineHeight: '20px',
                            content: 'attr(data-placeholder)',
                        },
                        '&.ql-error .ql-toolbar.ql-snow': {
                            borderColor: '#E45858',
                        },
                        '&.ql-error .ql-container.ql-snow': {
                            borderColor: '#E45858', 
                        }
                    }}
                >
                    {props.label && (
                        <FormLabel
                            sx={{
                                ...props.labelSx,
                            }}
                        >
                            {props.label}
                            {props.required && (
                                <Typography component="span" sx={{ color: 'error.main', ml: 0.5 }}>
                                    *
                                </Typography>
                            )}
                        </FormLabel>
                    )}
                    {props.viewOnly ? (
                        <Article
                            dangerouslySetInnerHTML={{
                                __html: props.viewText ? String(props.viewText) : value ? value : '-',
                            }}
                        />
                    ) : (
                        <ReactQuill
                            key={name}
                            value={value ?? ''}
                            id={name}
                            placeholder={props.placeholder}
                            modules={modules}
                            readOnly={props.disabled}
                            formats={formats}
                            theme="snow"
                            onChange={(text) => {
                                onChange(text);
                                props.onChange?.(text);
                            }}
                        />
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                        <FormHelperText error={Boolean(error)} sx={{ minHeight: 24, m: 0 }}>
                            {error?.message ?? ' '}
                        </FormHelperText>
                        {props.maxLength && (
                            <Typography sx={{ color: 'text.secondary', fontSize: 12, ml: 'auto' }}>
                                {stripHtml(value ?? '').length}/{props.maxLength} caractères
                            </Typography>
                        )}
                    </Box>
                </Box>
                </FormGroup>
            )}
        />
    );
};

export default InputRichText;
